import {
    Component, DestroyRef,
    HostBinding, inject, Inject,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {Observable} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import { environment } from '@env';
import packageJson from '../../package.json';
import OktaAuth, {AuthState} from '@okta/okta-auth-js';
import {OKTA_AUTH, OktaAuthStateService} from '@okta/okta-angular';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {SharedFacadeService} from '@emm/shared/services/shared-facade.service';
import {UserService} from '@emm/core/services/user.service';

const APP_NAME = 'Enterprise Menu Management';
const APP_SHORT_NAME = 'Menu';
const MANAGEMENT_TITLE = 'Menu Management';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-root',
    styleUrls: ['./app.component.scss'],
    templateUrl: './app.component.html',
    standalone: false
})
export class AppComponent implements OnInit {

    appName = APP_NAME;
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    shortName = APP_SHORT_NAME;
    private allTabs = [
        { label: 'Menu', url: '/menu', isDisabled: false },
        { label: 'Management', url: '/management', isDisabled: false },
        { label: 'Reports', url: '/reports', isDisabled: false },
    ];
    tabs = this.allTabs;
    version = packageJson.version;

    public isAuthenticated$!: Observable<boolean>;
    public userName$!: Observable<any>;

    @HostBinding('style.--nav-background-color')
    private navBackgroundColor: string;


    constructor(
        private titleService: Title,
        private authFacade: AuthFacadeService,
        private _oktaStateService: OktaAuthStateService,
        @Inject(OKTA_AUTH) private _oktaAuth: OktaAuth,
        private sharedFacadeService: SharedFacadeService,
        private userService: UserService,
    ) {
        _oktaAuth.tokenManager.on('expired', async (key) => {
            authFacade.renewToken(key).then();
        });
    }



    ngOnInit() {
        const theme = localStorage.getItem('theme');
        if (theme === null || theme === 'null') {
            localStorage.setItem('theme', 'light-theme');
        }

        const showTags = localStorage.getItem('showTags');
        if (showTags === null || showTags === 'null') {
            localStorage.setItem('showTags', 'true');
        }
        this.sharedFacadeService.initShowTags();
        this.sharedFacadeService.selectShowTags(localStorage.getItem('showTags'));

        this._oktaAuth.session.exists().then((exists) => {
            if (exists) {
                // Filter tabs based on user role
                this.filterTabsForUser();
                return;
            } else {
                this.signIn().then(() => {
                    this.authFacade.authenticateUser();
                    this.filterTabsForUser();
                });
            }
        });
        this.navBackgroundColor = environment.backgroundColor;
        this.titleService.setTitle(MANAGEMENT_TITLE);
        this.isAuthenticated$ = this._oktaStateService.authState$.pipe(
            filter((s: AuthState) => !!s),
            map((s: AuthState) => s.isAuthenticated ?? false)
        );
        this.userName$ = this._oktaStateService.authState$.pipe(
            filter((authState: AuthState) => !!authState && !!authState.isAuthenticated && !!authState.idToken),
            map((authState: AuthState) => {
                const cfaLogin = (authState.idToken.claims?.['cfa-login'] as string) ?? '';
                return cfaLogin
                    .split('.')
                    .map(part => part.replace(/\d/g, ''))
                    .filter(part => part.length > 0)
                    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                    .join(' ');
            })
        );
        this.userName$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    /**
     * Filter tabs to hide tabs for MMS Author-only users
     */
    private filterTabsForUser(): void {
        this.tabs = this.allTabs;
    }

    public async signOut() {
        this.logout().then(() => {
            this.authFacade.signOut();
        });
    }

    async logout() {
        this._oktaAuth.tokenManager.clear();
        this._oktaAuth.session.close().then();
        await this._oktaAuth.signOut();
    }

    public async signIn() : Promise<void> {
        await this._oktaAuth.signInWithRedirect();
    }
}
